import { FC, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  listGiftBrandSellerDeliveries,
  updateGiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.controller';
import {
  GiftDeliveryForAdmin,
  GiftDeliveryStatus,
} from '@shared/api/giftDelivery/giftDelivery.interface';
import { giftItemBrandListDataAtom } from '@shared/api/giftItemBrand/giftItemBrand.atom';
import { useGiftItemBrandListHook } from '@shared/api/giftItemBrand/giftItemBrand.hook';
import { Loading } from '@shared/components/Loading';
import { LowerRole } from '@shared/types';
import { useAsyncFn } from 'react-use';

import { GiftBrandThanksCardModal } from '../components/GiftBrandThanksCardModal';
import {
  nextStatusLabel,
  nextStatusMap,
  statusLabel,
  typeLabel,
} from '../../giftSeller/utils/delivery.constants';
import {
  formatPhone,
  getItemName,
  getOrdererInfo,
} from '../../giftSeller/utils/delivery.utils';

// 송장 입력 모달 상태 (준비중 -> 배송중)
interface ShippingModalState {
  deliveryId: string;
  carrier: string;
  trackingNumber: string;
}

const ACTIVE_STATUSES = [
  GiftDeliveryStatus.WaitingAddress,
  GiftDeliveryStatus.AddressSubmitted,
  GiftDeliveryStatus.Preparing,
  GiftDeliveryStatus.Shipping,
  // 취소 건도 목록에 포함되어 감사카드 조회가 가능하도록 필터 노출
  GiftDeliveryStatus.Canceled,
];

export const GiftBrandDeliveryManagePage: FC =
  function GiftBrandDeliveryManagePage() {
    const [statusFilter, setStatusFilter] = useState<GiftDeliveryStatus | 'all'>(
      'all',
    );
    const [searchText, setSearchText] = useState('');
    const [processingId, setProcessingId] = useState<string | null>(null);
    const [shippingModal, setShippingModal] = useState<ShippingModalState | null>(
      null,
    );
    const [cardDelivery, setCardDelivery] = useState<GiftDeliveryForAdmin | null>(
      null,
    );

    // 본인 브랜드의 감사카드 템플릿 (templateId 매칭용)
    useGiftItemBrandListHook(LowerRole.GiftBrandSeller);
    const brands = useRecoilValue(giftItemBrandListDataAtom);
    const thanksTemplates = useMemo(
      () => brands?.[0]?.thanksPrint?.templates ?? [],
      [brands],
    );

    const [{ loading, value: deliveries }, fetchDeliveries] = useAsyncFn(
      async () =>
        listGiftBrandSellerDeliveries({
          deliveryStatus: { $ne: GiftDeliveryStatus.Delivered },
        }),
      [],
    );

    useEffect(() => {
      fetchDeliveries();
    }, [fetchDeliveries]);

    const rows = useMemo(() => {
      let result = [...(deliveries ?? [])];
      if (statusFilter !== 'all') {
        result = result.filter((d) => d.deliveryStatus === statusFilter);
      }
      if (searchText) {
        const search = searchText.toLowerCase().replace(/-/g, '');
        result = result.filter((d) => {
          const phone = d.recipient?.phoneNumber?.replace(/-/g, '') || '';
          const name = d.recipient?.name?.toLowerCase() || '';
          const item = getItemName(d).toLowerCase();
          return (
            phone.includes(search) ||
            name.includes(search) ||
            item.includes(search)
          );
        });
      }
      return result;
    }, [deliveries, statusFilter, searchText]);

    // 다음 단계로 상태 변경
    const advanceStatus = async (delivery: GiftDeliveryForAdmin) => {
      const next = nextStatusMap[delivery.deliveryStatus];
      if (!next) return;

      // 준비중 -> 배송중 은 송장 정보를 받는다
      if (next === GiftDeliveryStatus.Shipping) {
        setShippingModal({
          deliveryId: delivery._id,
          carrier: delivery.shipping?.carrier || '',
          trackingNumber: delivery.shipping?.trackingNumber || '',
        });
        return;
      }

      setProcessingId(delivery._id);
      try {
        await updateGiftDeliveryStatus(
          delivery._id,
          next,
          next === GiftDeliveryStatus.Delivered
            ? { deliveredAt: new Date().toISOString() }
            : undefined,
          LowerRole.GiftBrandSeller,
        );
        await fetchDeliveries();
      } catch (err) {
        console.error(err);
        alert('상태 변경에 실패했습니다.');
      } finally {
        setProcessingId(null);
      }
    };

    const submitShipping = async () => {
      if (!shippingModal) return;
      setProcessingId(shippingModal.deliveryId);
      try {
        await updateGiftDeliveryStatus(
          shippingModal.deliveryId,
          GiftDeliveryStatus.Shipping,
          {
            carrier: shippingModal.carrier.trim() || undefined,
            trackingNumber: shippingModal.trackingNumber.trim() || undefined,
            shippedAt: new Date().toISOString(),
          },
          LowerRole.GiftBrandSeller,
        );
        setShippingModal(null);
        await fetchDeliveries();
      } catch (err) {
        console.error(err);
        alert('송장 등록에 실패했습니다.');
      } finally {
        setProcessingId(null);
      }
    };

    return (
      <div>
        <div className="mb-4">
          <h2 className="text-lg font-bold">주문 배송관리</h2>
          <p className="mt-1 text-sm text-gray-500">
            신규 주문을 발주 → 배송 → 완료 단계로 처리하세요.
          </p>
        </div>

        {/* 필터 */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as GiftDeliveryStatus | 'all')
            }
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="all">전체 상태</option>
            {ACTIVE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {statusLabel[s]?.text ?? s}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="수령인 · 연락처 · 상품명"
            className="flex-1 rounded-lg border px-3 py-2 text-sm"
          />
          <button
            onClick={() => fetchDeliveries()}
            className="rounded-lg border px-3 py-2 text-sm hover:bg-gray-50"
          >
            새로고침
          </button>
        </div>

        {loading ? (
          <div className="center-box py-16">
            <Loading />
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
            처리할 배송 건이 없습니다.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg bg-white shadow">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">상태</th>
                  <th className="px-3 py-2 text-left">코드</th>
                  <th className="px-3 py-2 text-left">상품</th>
                  <th className="px-3 py-2 text-left">수량</th>
                  <th className="px-3 py-2 text-left">수령인</th>
                  <th className="px-3 py-2 text-left">배송지</th>
                  <th className="px-3 py-2 text-left">주문자</th>
                  <th className="px-3 py-2 text-left">처리 / 감사카드</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((d) => {
                  const orderer = getOrdererInfo(d);
                  const badge = statusLabel[d.deliveryStatus] ?? {
                    text: d.deliveryStatus,
                    className: 'bg-gray-100 text-gray-600',
                  };
                  const canAdvance = !!nextStatusMap[d.deliveryStatus];
                  return (
                    <tr key={d._id} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-3 py-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badge.className}`}
                        >
                          {badge.text}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3 font-mono text-xs text-gray-500">
                        {d.fullCode || d.code || '-'}
                      </td>
                      <td className="px-3 py-3">
                        <p className="max-w-[160px] truncate font-medium">
                          {getItemName(d)}
                        </p>
                        <span
                          className={`text-[10px] ${typeLabel[d.type] ? '' : ''}`}
                        >
                          {typeLabel[d.type] || d.type}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3">{d.quantity}개</td>
                      <td className="whitespace-nowrap px-3 py-3">
                        <p className="font-medium">{d.recipient?.name || '-'}</p>
                        <p className="text-xs text-gray-500">
                          {formatPhone(d.recipient?.phoneNumber)}
                        </p>
                      </td>
                      <td className="max-w-[200px] px-3 py-3">
                        {d.recipient?.address ? (
                          <p className="truncate text-xs text-gray-600">
                            ({d.recipient.postalCode}) {d.recipient.address}{' '}
                            {d.recipient.addressDetail || ''}
                          </p>
                        ) : (
                          <span className="text-xs text-gray-400">미입력</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-3">
                        <p>{orderer.name}</p>
                        <p className="text-xs text-gray-500">
                          {formatPhone(orderer.phone)}
                        </p>
                      </td>
                      <td className="whitespace-nowrap px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          {canAdvance ? (
                            <button
                              onClick={() => advanceStatus(d)}
                              disabled={processingId === d._id}
                              className="rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                            >
                              {processingId === d._id
                                ? '처리중...'
                                : nextStatusLabel[d.deliveryStatus] || '상태 변경'}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">대기</span>
                          )}
                          {d.thanksPrint && (
                            <button
                              onClick={() => setCardDelivery(d)}
                              className="rounded border border-rose-300 px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50"
                            >
                              감사카드
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* 송장 입력 모달 */}
        {shippingModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl">
              <h3 className="mb-4 text-base font-bold">송장 정보 입력</h3>
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-sm font-semibold">택배사</label>
                  <input
                    value={shippingModal.carrier}
                    onChange={(e) =>
                      setShippingModal({
                        ...shippingModal,
                        carrier: e.target.value,
                      })
                    }
                    placeholder="예: CJ대한통운"
                    className="w-full rounded border px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-semibold">송장번호</label>
                  <input
                    value={shippingModal.trackingNumber}
                    onChange={(e) =>
                      setShippingModal({
                        ...shippingModal,
                        trackingNumber: e.target.value,
                      })
                    }
                    placeholder="송장번호"
                    className="w-full rounded border px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="mt-5 flex gap-2">
                <button
                  onClick={() => setShippingModal(null)}
                  className="flex-1 rounded-lg border px-4 py-2 text-sm hover:bg-gray-50"
                >
                  취소
                </button>
                <button
                  onClick={submitShipping}
                  disabled={processingId === shippingModal.deliveryId}
                  className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  배송중으로 변경
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 감사카드 미리보기 / 인쇄 */}
        <GiftBrandThanksCardModal
          delivery={cardDelivery}
          templates={thanksTemplates}
          onClose={() => setCardDelivery(null)}
        />
      </div>
    );
  };
